import React, { useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { Form, Icon, Input, Button, message, Spin } from "antd";
import { connect } from "react-redux";
import DocumentTitle from "react-document-title";
import "./index.less";
import { login, getUserInfo } from "@/store/actions";

const AnimatedLoginBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pointer = { x: null, y: null };
    const colors = ["#7dd3fc", "#34d399", "#60a5fa", "#f8fafc"];
    let width = 0;
    let height = 0;
    let particles = [];
    let frameId;

    const createParticle = () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      radius: Math.random() * 1.7 + 0.8,
      color: colors[Math.floor(Math.random() * colors.length)],
    });

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(92, Math.max(48, Math.floor(width / 18)));
      particles = Array.from({ length: count }, createParticle);
    };

    const drawGrid = () => {
      ctx.save();
      ctx.strokeStyle = "rgba(148, 163, 184, 0.08)";
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 42) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += 42) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#07111f");
      gradient.addColorStop(0.45, "#102a3a");
      gradient.addColorStop(1, "#0b1f1a");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      drawGrid();

      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < -20) particle.x = width + 20;
        if (particle.x > width + 20) particle.x = -20;
        if (particle.y < -20) particle.y = height + 20;
        if (particle.y > height + 20) particle.y = -20;

        for (let j = index + 1; j < particles.length; j += 1) {
          const target = particles[j];
          const dx = particle.x - target.x;
          const dy = particle.y - target.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 122) {
            ctx.strokeStyle = `rgba(125, 211, 252, ${0.16 * (1 - distance / 122)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(target.x, target.y);
            ctx.stroke();
          }
        }

        if (pointer.x !== null) {
          const dx = particle.x - pointer.x;
          const dy = particle.y - pointer.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 170) {
            ctx.strokeStyle = `rgba(52, 211, 153, ${0.28 * (1 - distance / 170)})`;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
          }
        }

        ctx.fillStyle = particle.color;
        ctx.shadowBlur = 12;
        ctx.shadowColor = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      frameId = window.requestAnimationFrame(draw);
    };

    const handleMouseMove = (event) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
    };

    const handleMouseLeave = () => {
      pointer.x = null;
      pointer.y = null;
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return <canvas className="login-bg-canvas" ref={canvasRef} />;
};

const Login = (props) => {
  const { form, token, login, getUserInfo } = props;
  const { getFieldDecorator } = form;

  const [loading, setLoading] = useState(false);

  const handleLogin = (username, password) => {
    setLoading(true);
    login(username, password)
      .then((data) => {
        message.success("登录成功");
        return getUserInfo(data.token);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error || "登录失败");
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        const { username, password } = values;
        handleLogin(username, password);
      }
    });
  };

  if (token) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <DocumentTitle title="量角后台登录">
      <div className="login-page">
        <AnimatedLoginBackground />
        <div className="login-shell">
          <div className="login-brand">
            <div className="login-brand-mark">
              <Icon type="dashboard" />
            </div>
            <div>
              <h1>量角后台系统</h1>
              <p>图床、反馈与账号运营管理</p>
            </div>
          </div>

          <div className="login-panel">
            <div className="login-panel-header">
              <h2>登录</h2>
              <span>请输入管理员账号继续</span>
            </div>
            <Spin spinning={loading} tip="登录中...">
              <Form onSubmit={handleSubmit} className="login-form">
                <Form.Item>
                  {getFieldDecorator("username", {
                    rules: [
                      {
                        required: true,
                        whitespace: true,
                        message: "请输入用户名",
                      },
                    ],
                  })(
                    <Input
                      size="large"
                      prefix={<Icon type="user" />}
                      placeholder="用户名"
                    />
                  )}
                </Form.Item>
                <Form.Item>
                  {getFieldDecorator("password", {
                    rules: [
                      {
                        required: true,
                        whitespace: true,
                        message: "请输入密码",
                      },
                    ],
                  })(
                    <Input.Password
                      size="large"
                      prefix={<Icon type="lock" />}
                      placeholder="密码"
                    />
                  )}
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="login-submit"
                >
                  登录系统
                </Button>
              </Form>
            </Spin>
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
};

const WrapLogin = Form.create()(Login);

export default connect((state) => state.user, { login, getUserInfo })(
  WrapLogin
);
